import * as React from 'react'
import { Link } from 'react-router-dom'
import { Building2, Image as ImageIcon, Settings, Plus } from 'lucide-react'
import { useVenues, useCreateVenue } from '@/hooks/useVenues'
import { useAuth } from '@/auth/AuthContext'
import { LoginToVenueButton } from '@/components/LoginToVenueButton'
import { can, reachesCompany } from '@/auth/access'
import { seesVenueSettings } from '@/components/appNav'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import { objectPositionOf } from '@lib/helpers/focal-point'
import { cn } from '@/lib/utils'
import { Tooltip } from '@/components/ui/tooltip'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { EmptyState, ErrorState, ListSkeleton } from '@/components/ui/states'
import { Pagination } from '@/components/ui/pagination'
import { serverPage } from '@/lib/pagination'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { createVenueInputSchema } from '@lib/input/venue.input'
import type { VenueDto, VenueListDto } from '@lib/dto/venue.dto'
import { Facts } from '@/components/ui/facts'
import { FormError } from '@/components/ui/form-error'
import { venueFacts } from '@/lib/venueFacts'

/** New-venue form: just the venue's own fields (name + address). Spaces, tracks,
 * and inventory are added afterward on the venue's page. */
function VenueCreateForm({ onDone }: { onDone: () => void }) {
  const [name, setName] = React.useState('')
  const [address, setAddress] = React.useState('')
  const create = useCreateVenue()

  function save(): void {
    const trimmed = name.trim()
    if (!trimmed) return
    const nextAddress = address.trim()
    // Through the shared schema, per `web/CLAUDE.md`: it fills in the usual
    // evening a new venue keeps, so the browser sends what the server would
    // have defaulted rather than leaving the pair half-stated.
    const parsed = createVenueInputSchema.safeParse({
      name: trimmed,
      address: nextAddress === '' ? null : nextAddress,
    })
    if (!parsed.success) return
    create.mutate(parsed.data, { onSuccess: onDone })
  }

  return (
    <>
      <div className="space-y-5">
        <Field label="Name" htmlFor="new-venue-name">
          <Input id="new-venue-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Venue name" autoFocus />
        </Field>
        <Field label="Address" htmlFor="new-venue-address">
          <Input
            id="new-venue-address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Address (optional)"
          />
        </Field>
        {create.isError ? <FormError>{requestErrorMessage(create.error)}</FormError> : null}
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button pending={create.isPending} type="button" onClick={save} disabled={name.trim() === ''}>
          {create.isPending ? 'Creating…' : 'Create venue'}
        </Button>
      </DialogFooter>
    </>
  )
}

/** Create a venue from a dialog. Mounted-only-while-open so fields reset. */
function VenueCreateDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New venue</DialogTitle>
          <DialogDescription>Add a venue to your organization. You can add spaces and inventory afterward.</DialogDescription>
        </DialogHeader>
        {open ? <VenueCreateForm onDone={() => onOpenChange(false)} /> : null}
      </DialogContent>
    </Dialog>
  )
}

/**
 * One venue as a card, on Plan View's venue-card recipe: its photograph framed
 * at 16:10 above the name — never under it — then where it is and what it has,
 * and the actions in a footer of their own, since a button nested inside the
 * card's link would navigate instead of doing its job.
 *
 * A venue with no photograph shows the empty well on the plan grid. It used to
 * get one of three stock manors, so a page of new venues showed the same
 * building three times over, each captioned with a different name.
 */
function VenueCard({ venue }: { venue: VenueDto }) {
  const { user } = useAuth()
  const cover = venue.coverImageUrl
  return (
    <div className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-line bg-surface transition-shadow duration-150 hover:shadow-md">
      <Link to={`/venues/${venue.id}`} className="block rounded-t-lg outline-none focus-visible:focus-ds">
        <div
          className={cn(
            'relative grid aspect-[16/10] place-items-center overflow-hidden bg-surface-sunken text-ink-muted',
            cover ? undefined : 'plan-grid',
          )}
        >
          {cover ? (
            <img
              src={cover}
              alt=""
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover"
              style={{ objectPosition: objectPositionOf(venue.coverFocalPoint ?? null, '50% 40%') }}
            />
          ) : (
            <span className="grid justify-items-center gap-1.5 text-small">
              <ImageIcon className="h-5 w-5 text-ink-subtle" aria-hidden />
              No photo yet
            </span>
          )}
        </div>
        <div className="grid gap-2 px-4 pb-3 pt-3.5">
          <p className="m-0 truncate text-heading font-semibold leading-5 text-ink">{venue.name}</p>
          {/* One line per fact, the same lines on every card: a venue with no
              address says so on the address line rather than lifting its space
              count into it. */}
          <Facts layout="stack" facts={venueFacts(venue)} className="text-control" />
        </div>
      </Link>
      <div className="mt-auto flex items-center justify-end gap-2 border-t border-line px-3 py-2.5">
        <LoginToVenueButton venueId={venue.id} size="sm" />
        {/* A cog, not a chevron: this goes to the venue's settings, and a
            chevron promises the next item in a list. The card itself opens the
            venue's dashboard — the overview an organization admin came for. */}
        {seesVenueSettings(user) ? (
          <Tooltip content={`${venue.name} settings`}>
            <Link
              to={`/venues/${venue.id}/settings`}
              aria-label={`${venue.name} settings`}
              className="grid h-[30px] w-[30px] place-items-center rounded-md text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink focus-visible:focus-ds"
            >
              <Settings className="h-4 w-4" aria-hidden />
            </Link>
          </Tooltip>
        ) : null}
      </div>
    </div>
  )
}

/** Venues list (tenant-scoped) with loading / empty / error handling. */
export function VenuesPage() {
  const { user } = useAuth()
  const canCreateVenue = can(user, 'venues:edit') && reachesCompany(user)
  const [creating, setCreating] = React.useState(false)
  const [page, setPage] = React.useState(1)
  const venuesQuery = useVenues(page)

  function renderContent() {
    if (venuesQuery.isPending) {
      return <ListSkeleton rows={4} />
    }

    if (venuesQuery.isError) {
      return (
        <ErrorState message={requestErrorMessage(venuesQuery.error)} onRetry={() => void venuesQuery.refetch()} />
      )
    }

    const list: VenueListDto = venuesQuery.data
    // `total`, not the row count: an out-of-range page comes back with no rows,
    // and keying the empty state off that would tell somebody their catalog was
    // empty when it is only this page that is.
    if (list.total === 0) {
      return (
        <EmptyState
          icon={Building2}
          title="No venues yet"
          description="Venues for your organization will appear here."
          {...(canCreateVenue
            ? {
                action: (
                  <Button onClick={() => setCreating(true)}>
                    <Plus className="h-4 w-4" /> New venue
                  </Button>
                ),
              }
            : {})}
        />
      )
    }

    // Auto-fill rather than breakpoint steps: the column count follows the width
    // it actually has, so a wider display buys more photographs at a readable
    // size instead of four increasingly enormous cards.
    // The rows are the page the API returned; `total` is the whole list.
    const shown = serverPage(page, list.total, list.venues.length)
    return (
      <div>
        <div className="ds-stagger grid gap-4 grid-cols-[repeat(auto-fill,minmax(min(100%,20rem),1fr))]">
          {list.venues.map((venue) => (
            <VenueCard key={venue.id} venue={venue} />
          ))}
        </div>
        {/* No panel to sit inside here — a grid of cards has no border — so the
            bar's own top hairline is what separates it from the last row. */}
        <Pagination
          page={shown.page}
          pageCount={shown.pageCount}
          from={shown.from}
          to={shown.to}
          total={shown.total}
          unit="venues"
          onPage={setPage}
          className="mt-4 px-0"
        />
      </div>
    )
  }

  return (
    <PageContainer width="wide" className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          title="Venues"
          description="Your organization’s venues and the spaces in each."
        />
        {canCreateVenue ? (
          <Button onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" /> New venue
          </Button>
        ) : null}
      </div>
      {renderContent()}
      <VenueCreateDialog open={creating} onOpenChange={setCreating} />
    </PageContainer>
  )
}
